import { Fragment, ReactNode } from 'react';

const BULLET = /^[-*]\s+/;
const NUMBERED = /^\d+[.)]\s+/;
// Only help article URLs become links, so the model cannot send the user anywhere else
const HELP_URL = /(https:\/\/help\.paidnice\.com\/[^\s)]*[^\s).,])/g;

function links(text: string): ReactNode[] {
  return text.split(HELP_URL).map((part, index) => (
    index % 2 === 1
      ? (
        <a
          key={ index }
          href={ part }
          target={ `_blank` }
          rel={ `noreferrer` }
          className={ `cursor-pointer text-paidniceGreen-600 hover:text-paidniceGreen-700 underline break-all` }
        >
          { part }
        </a>
      )
      : <Fragment key={ index }>{ part }</Fragment>
  ));
}

/** `**bold**` and help article URLs only; everything else stays literal text so nothing the model writes can inject markup */
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => (
    part.startsWith(`**`) && part.endsWith(`**`) && part.length > 4
      ? <strong key={ index }>{ part.slice(2, -2) }</strong>
      : <Fragment key={ index }>{ links(part) }</Fragment>
  ));
}

/**
 * The small Markdown subset the assistant prompt allows: paragraphs separated by blank
 * lines, `-` / `1.` lists, `**bold**` and plain help article URLs. Renders React nodes, never HTML strings.
 */
export function AssistantMarkdown({ content }: { content: string }) {
  const blocks = content.trim().split(/\n\s*\n/);

  return (
    <div className={ `space-y-2` }>
      {
        blocks.map((block, index) => {
          const lines = block.split(`\n`).map(line => line.trim()).filter(Boolean);
          const listPattern = lines.every(line => BULLET.test(line)) ? BULLET : lines.every(line => NUMBERED.test(line)) ? NUMBERED : null;

          if (listPattern) {
            const List = listPattern === BULLET ? `ul` : `ol`;

            return (
              <List
                key={ index }
                className={ listPattern === BULLET ? `list-disc space-y-1 pl-5` : `list-decimal space-y-1 pl-5` }
              >
                { lines.map((line, lineIndex) => <li key={ lineIndex }>{ inline(line.replace(listPattern, ``)) }</li>) }
              </List>
            );
          }

          return (
            <p
              key={ index }
              className={ `whitespace-pre-wrap` }
            >
              { inline(lines.join(`\n`)) }
            </p>
          );
        })
      }
    </div>
  );
}
